import { useMemo, useRef, useState } from "react";
import { Flex, Spin } from "antd";
import { useCustom, useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { LoadingOutlined } from "@ant-design/icons";
import { ImageIcon, VideoIcon } from "@phosphor-icons/react";
import { PicturePreview } from "pages/media/brandkit/components/PicturePreview";
import { InfiniteScrollList } from "../controls/InfiniteScrollList";
import {
  StockPhoto,
  StockPhotoResponse,
  StockVideo,
  StockVideoResponse,
} from "pages/media/types";
import VideoPreview from "pages/media/brandkit/components/VideoPreview";
import { Empty } from "components/Empty";
import { AssetType } from "pages/asset-libraries/useMediaAssets/";
import { AssetControlsOverlay } from "../controls/AssetControlsOverlay";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import {
  AspectRatioValue,
  Orientation,
  OrientationSelect,
} from "../controls/OrientationSelect";
import { AssetWrapperButton } from "./AssetWrapperButton";
import SearchInput from "pages/media/projects/components/SearchInput/";
import { useMasonryGalleryLayout } from "../hooks/useMasonryGalleryLayout";
import { MediaKind } from "../../../../types";

type StockAsset = StockPhoto | StockVideo;

const isStockPhoto = (
  items: StockPhoto[] | StockVideo[]
): items is StockPhoto[] => {
  return items.every((item) => "original_url" in item);
};
const isStockVideo = (
  items: StockPhoto[] | StockVideo[]
): items is StockVideo[] => {
  return items.every((item) => "preview_url" in item);
};

const getStockAspectRatio = (item: StockAsset): number | null => {
  if (item.width && item.height && item.width > 0 && item.height > 0) {
    return item.width / item.height;
  }
  return null;
};

export const StockAssetsSearch = ({
  currentAssetLibraryId,
  assetType,
  previewWidth,
  previewHeight,
  customAddToCanvas,
  onReplaceSelectedAsset,
  selectionMode,
}: {
  currentAssetLibraryId: string;
  assetType: AssetType;
  previewWidth: number;
  previewHeight: number;
  customAddToCanvas?: (assetId: string, path: string) => void;
  onReplaceSelectedAsset?: (
    assetId: string,
    path: string,
    mediaType: MediaKind
  ) => void;
  selectionMode?: boolean;
}) => {
  const [inputValue, setInputValue] = useState<string>(""); // What user sees in input
  const [searchQuery, setSearchQuery] = useState<string | null>(null); // Triggers API
  const [page, setPage] = useState(1);
  const [orientation, setOrientation] = useState<Orientation>("all");
  const [accumulatedAssets, setAccumulatedAssets] = useState<
    StockPhoto[] | StockVideo[]
  >([]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const t = useTranslate();

  const [assetIsBeingAdded, setAssetIsBeingAdded] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const {
    mediaAssetsData: { addStockAsset },
  } = useTemplateData();

  const handleAddStockAsset = async (value: StockPhoto | StockVideo) => {
    const newItem = await addStockAsset(
      value,
      currentAssetLibraryId,
      (isLoading) => setAssetIsBeingAdded(isLoading)
    );

    return newItem;
  };

  const handleSelectAsMain = async (value: StockPhoto | StockVideo) => {
    if (!onReplaceSelectedAsset) return;
    const newItem = await handleAddStockAsset(value);
    if (newItem?.assetId && newItem.path) {
      const mediaType: MediaKind =
        assetType === "stock_videos" ? "video" : "image";
      onReplaceSelectedAsset(newItem.assetId, newItem.path, mediaType);
    }
  };

  const handleQuerySuccess = (
    responseData: StockPhotoResponse | StockVideoResponse
  ) => {
    let stockAssets: StockPhoto[] | StockVideo[] = [];
    if ("photos" in responseData) {
      stockAssets = responseData.photos;
    } else if ("videos" in responseData) {
      stockAssets = responseData.videos;
    }

    setAccumulatedAssets((prev) => {
      const existingIds = new Set(prev.map((item) => item.item_id));
      const newItems = stockAssets.filter(
        (item) => !existingIds.has(item.item_id)
      );
      if (isStockVideo(prev) && isStockVideo(stockAssets)) {
        return [...prev, ...newItems] as StockVideo[];
      } else if (isStockPhoto(prev) && isStockPhoto(stockAssets)) {
        return [...prev, ...newItems] as StockPhoto[];
      }
      return prev;
    });
  };

  const orientationFilter = useMemo(() => {
    if (orientation === AspectRatioValue.Desktop) return "landscape";
    if (orientation === AspectRatioValue.Mobile) return "portrait";
    return undefined;
  }, [orientation]);

  const {
    data,
    isFetching: areAssetsFetching,
    isLoading: areAssetsLoading,
  } = useCustom<StockPhotoResponse | StockVideoResponse>({
    url: `media/asset_libraries/${currentAssetLibraryId}/${assetType}`,
    method: "get",
    queryOptions: {
      enabled: !!searchQuery && !!currentAssetLibraryId,
      onSuccess: (response) => handleQuerySuccess(response.data),
    },
    config: {
      filters: [
        {
          field: "provider",
          operator: "eq",
          value: "pexels",
        },
        {
          field: "query",
          operator: "eq",
          value: searchQuery,
        },
        {
          field: "page",
          operator: "eq",
          value: page,
        },
        { field: "per_page", operator: "eq", value: 10 },
        {
          field: "orientation",
          operator: "eq",
          value: orientationFilter,
        },
      ],
    },
  });

  const totalResults = useMemo(
    () => data?.data.total_results ?? 0,
    [data?.data.total_results]
  );

  const resetSearch = () => {
    setPage(1);
    setAccumulatedAssets([]);
  };

  const handleSubmit = () => {
    if (!inputValue.trim()) return;
    resetSearch();
    setSearchQuery(inputValue.trim());
  };

  const hasMore = totalResults > 0 && accumulatedAssets.length < totalResults;

  const { galleryRef, breakpoints, galleryAssets, groupBy, renderGroup } =
    useMasonryGalleryLayout<StockAsset>({
      items: accumulatedAssets,
      getAspectRatio: getStockAspectRatio,
    });

  const scrollableTargetId = `video-editor-panel-${assetType === "stock_videos" ? "videos" : "images"}`;

  return (
    <Flex vertical style={{ height: "100%", overflow: "hidden" }}>
      <Flex
        role="header"
        gap={10}
        justify="center"
        style={{ marginBottom: 16 }}
      >
        <SearchInput
          placeholder={
            assetType === "stock_photos"
              ? t("asset_libraries.searchImage")
              : t("asset_libraries.searchVideo")
          }
          allowClear
          onPressEnter={handleSubmit}
          size="middle"
          onSearch={setInputValue}
          value={inputValue}
        />
        <OrientationSelect
          value={orientation}
          showLabel={false}
          onChange={(orientation: Orientation) => {
            setOrientation(orientation);
            resetSearch();
          }}
        />
      </Flex>
      {searchQuery && (
        <StyledPexelsAttribution
          href="https://www.pexels.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          {assetType === "stock_videos"
            ? t("asset_libraries.videosProvidedByPexels")
            : t("asset_libraries.photosProvidedByPexels")}
        </StyledPexelsAttribution>
      )}
      <Flex
        ref={scrollRef}
        vertical
        gap={20}
        id={scrollableTargetId}
        style={{ overflow: "auto", height: "100%" }}
      >
        {accumulatedAssets.length === 0 &&
          searchQuery &&
          !areAssetsFetching &&
          !areAssetsLoading && (
            <Empty
              image={
                assetType === "stock_photos" ? (
                  <ImageIcon size={100} />
                ) : (
                  <VideoIcon size={100} />
                )
              }
              description={t("asset_libraries.searchNoResults")}
            />
          )}
        <Spin
          spinning={
            accumulatedAssets.length === 0 &&
            areAssetsFetching &&
            areAssetsLoading
          }
          indicator={<LoadingOutlined style={{ fontSize: 60 }} />}
        />
        <div ref={galleryRef} style={{ width: "100%" }}>
          <InfiniteScrollList
            assetLength={galleryAssets.length}
            dataSource={galleryAssets}
            rowKey="item_id"
            hasMore={hasMore}
            breakpoints={breakpoints}
            groupBy={groupBy}
            renderGroup={renderGroup}
            onNext={() => {
              setPage((prev) => {
                return prev + 1;
              });
            }}
            scrollableTarget={scrollableTargetId}
          >
            {(item: StockAsset) => {
              return (
                <Flex
                  gap={5}
                  vertical
                  className="gallery-item"
                  style={{ width: "100%" }}
                >
                  <AssetWrapperButton
                    onMouseEnter={() => setHoveredItemId(item.item_id)}
                    onMouseLeave={() => setHoveredItemId(null)}
                    onClick={
                      onReplaceSelectedAsset
                        ? (e) => {
                            e.stopPropagation();
                            void handleSelectAsMain(item);
                          }
                        : undefined
                    }
                    style={{
                      width: "100%",
                      opacity: assetIsBeingAdded ? 0.5 : 1,
                      pointerEvents: assetIsBeingAdded ? "none" : "auto",
                      cursor: onReplaceSelectedAsset ? "pointer" : "default",
                    }}
                  >
                    {"duration" in item ? (
                      item.preview_url ? (
                        <VideoPreview
                          key={item.item_id}
                          video={item.preview_url}
                          duration={item.duration ?? 0}
                          fileName=""
                          minHeight="auto"
                          width="100%"
                        />
                      ) : null
                    ) : item.thumbnail_url ? (
                      <PicturePreview
                        key={item.item_id}
                        cardWidth="100%"
                        coverHeight={"fit-content"}
                        image={item.thumbnail_url}
                        fileName=""
                        meta={false}
                      />
                    ) : null}
                    {(selectionMode ||
                      hoveredItemId === item.item_id) && (
                      <AssetControlsOverlay
                        item={item}
                        isVideo={assetType === "stock_videos"}
                        onUploadAsset={handleAddStockAsset}
                        previewWidth={previewWidth}
                        previewHeight={previewHeight}
                        customAddToCanvas={customAddToCanvas}
                        selectionMode={selectionMode}
                      />
                    )}
                  </AssetWrapperButton>
                </Flex>
              );
            }}
          </InfiniteScrollList>
        </div>
      </Flex>
    </Flex>
  );
};

const StyledPexelsAttribution = styled.a`
  margin-bottom: 8px;
  color: inherit;
  text-decoration: none;
  font-size: 13px;
  align-self: center;
  opacity: 0.7;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colorPrimary};
    text-decoration: none;
    opacity: 1;
  }
`;
